import { useEffect, useRef, useState } from "react";
import { useCheckoutLogic } from "@/src/hooks/useCheckoutLogic";
import { useCheckoutForm } from "@/src/hooks/useCheckoutForm";
import { useAddressAutocomplete } from "@/src/hooks/useAddressAutocomplete";
import { usePayment } from "@/src/hooks/usePayment";
import { useCartTimer } from "@/src/hooks/useCartTimer";
import { CheckoutHeader } from "@/src/components/checkout/CheckoutHeader";
import { BundleSelection } from "@/src/components/checkout/BundleSelection";
import { PaymentForm } from "@/src/components/checkout/PaymentForm";
import { ContactForm } from "@/src/components/checkout/ContactForm";
import { ShippingAddressForm } from "@/src/components/checkout/ShippingAddressForm";
import OrderBump from "@/src/components/checkout/OrderBump";
import { Review } from "./components/checkout/Review";
import { OrderSummary } from "./components/checkout/OrderSummary";
import TrusteReviews from "./components/checkout/TrusteReviews";
import FAQ from "./components/checkout/FAQ";
import CheckoutFooter from "./components/checkout/CheckoutFooter";

interface CheckoutPageProps {
  checkoutToken: string;
}

export function CheckoutPage({ checkoutToken }: CheckoutPageProps) {
  const {
    checkout,
    refresh,
    pluginConfig,
    bundles,
    isUpdating,
    handleBundleChange,
    isLoading,
    lineItems,
    selectedBundle,
    handleOrderBumpToggle,
    orderBumpsInCheckout,
    orderBumpMutationPending,
    couponCodeInput,
    setCouponCodeInput,
    couponDisplayLabel,
    isApplyingCoupon,
    couponError,
    canRemoveCoupon,
    applyCoupon,
    removeCoupon,
    prePurchaseCheckout,
    hasOptimisticOverlay,
    getCheckout,
  } = useCheckoutLogic({
    checkoutToken,
  });

  const { form, flushAndSaveCheckoutInfo } = useCheckoutForm({ checkoutToken });

  const addressAutocomplete = useAddressAutocomplete({
    pluginConfig,
    setValue: form.setValue,
    watch: form.watch,
  });

  const { handlePayment, isPaymentLoading, paymentError } = usePayment({
    checkout,
    getCheckout,
    form,
    pluginConfig,
    prePurchaseCheckout,
    refresh,
    flushAndSaveCheckoutInfo,
  });

  const { formatCartTimer } = useCartTimer();

  const [showStickyButton, setShowStickyButton] = useState(false);
  const paymentSectionRef = useRef<HTMLDivElement>(null);

  // Google Tag Manager initialization
  useEffect(() => {
    const w = window as any;
    const d = document;
    const s = "script";
    const l = "dataLayer";
    const i = "GTM-W63XBPBL";

    w[l] = w[l] || [];
    w[l].push({ "gtm.start": Date.now(), event: "gtm.js" });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dl = l === "dataLayer" ? "" : "&l=" + l;
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode?.insertBefore(j, f);
  }, []);

  // Mobile sticky button visibility
  useEffect(() => {
    const handleScroll = () => {
      if (!paymentSectionRef.current) return;
      const rect = paymentSectionRef.current.getBoundingClientRect();
      const isPaymentSectionVisible =
        rect.top < window.innerHeight && rect.bottom > 0;
      const isMobile = window.innerWidth < 1024;
      setShowStickyButton(isMobile && !isPaymentSectionVisible);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const selectedBundleData = bundles.find((b) => b.id === selectedBundle);
  const percentageSavings = selectedBundleData?.percentageSavings || 0;

  return (
    <div className=" font-montserrat  ">
      <CheckoutHeader companyName={pluginConfig.branding.companyName} />

      <main className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 border border-gray-200 shadow-md p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-2">
          {/* Left Column */}
          <div className="border border-gray-300 p-3 sm:p-4 md:p-5 space-y-3 sm:space-y-4 md:space-y-5">
            <BundleSelection
              bundles={bundles}
              selectedBundle={selectedBundle}
              isUpdating={isUpdating}
              onBundleChange={handleBundleChange}
              pluginConfig={pluginConfig}
              percentageSavings={percentageSavings}
              cartTimer={formatCartTimer()}
            />

            <OrderBump
              orderBumpsInCheckout={orderBumpsInCheckout}
              offerId={pluginConfig.orderBumpIdExpeditedShipping}
              variantId={pluginConfig.orderBumpIdExpeditedShippingVariant}
              handleOrderBumpToggle={handleOrderBumpToggle}
              disabled={isUpdating || orderBumpMutationPending}
              title="Yes, I want Expedited Shipping for ONLY $9.95!"
              description={
                <div className="flex flex-row gap-2 sm:gap-3 md:gap-4">
                  <img
                    src="//fnsh.imgix.net/16391/132009/1694055488-1688312587252_1_1_.png?auto=compress,format&fit=scale&w=96&h=87"
                    crossOrigin="anonymous"
                    alt="Expedited shipping"
                    className="w-16 h-auto  md:h-24 sm:w-20 md:w-24 shrink-0 "
                  />
                  <div className="flex-1 min-w-0">
                    <strong className="block text-[10px] sm:text-xs md:text-[13px] text-[rgb(200,32,47)] mb-0.5 sm:mb-1 font-bold">
                      EXPEDITED SHIPPING:
                    </strong>
                    <span className="block text-[10px] sm:text-xs md:text-sm text-[rgb(33,37,41)] leading-snug sm:leading-relaxed">
                      Want express shipping with your order? Select the
                      Expedited Shipping option for just $9.95. Exclusive
                      shipping and tracking service with delivery time up to 5-8
                      business days!
                    </span>
                  </div>
                </div>
              }
            />

            <OrderBump
              orderBumpsInCheckout={orderBumpsInCheckout}
              offerId={pluginConfig.orderBumpIdLifetimeProtection}
              variantId={pluginConfig.orderBumpIdLifetimeProtectionVariant}
              handleOrderBumpToggle={handleOrderBumpToggle}
              disabled={isUpdating || orderBumpMutationPending}
              title="Yes, I want Extended Lifetime Protection for ONLY $19.95!"
              description={
                <div className="flex flex-row gap-2 sm:gap-3 md:gap-4">
                  <img
                    src="//fnsh.imgix.net/16391/132009/1694055494-1688313716977_1.jpg?auto=compress,format&fit=scale&w=96&h=91"
                    crossOrigin="anonymous"
                    alt="Lifetime Protection"
                    className="w-16 h-auto md:h-24 sm:w-20 md:w-24 shrink-0 "
                  />
                  <div className="flex-1 min-w-0">
                    <strong className="block text-[10px] sm:text-xs md:text-[13px] text-[rgb(200,32,47)] mb-0.5 sm:mb-1 font-bold">
                      LIFETIME PROTECTION:
                    </strong>
                    <span className="block text-[10px] sm:text-xs md:text-sm text-[rgb(33,37,41)] leading-snug sm:leading-relaxed">
                      Coverage for accidental damages, including drops, spills,
                      and broken parts, as well as breakdowns. 24/7 support when
                      you need it. Quick, easy, and frustration-free claims.
                      Applies To All Devices In Your Cart
                    </span>
                  </div>
                </div>
              }
            />

            <ContactForm form={form} />

            <ShippingAddressForm
              form={form}
              countries={addressAutocomplete.countries}
              availableStates={addressAutocomplete.availableStates}
              isCountrySelected={addressAutocomplete.isCountrySelected}
              addressInput={addressAutocomplete.addressInput}
              showPredictions={addressAutocomplete.showPredictions}
              predictions={addressAutocomplete.predictions}
              onAddressInputChange={
                addressAutocomplete.handleAddressInputChange
              }
              onPredictionSelect={addressAutocomplete.handlePredictionSelect}
              onCountryChange={addressAutocomplete.handleCountryChange}
              sameBillingAddress={form.watch("sameBillingAddress")}
              onSameBillingAddressChange={(checked) => {
                form.setValue("sameBillingAddress", checked);
              }}
            />
          </div>

          {/* Right Column - Order Summary */}
          <div className="border border-gray-300 p-3 sm:p-4 md:p-5 space-y-4 sm:space-y-5 md:space-y-6">
            <div className="mb-2.5 flex items-center gap-3">
              <h3 className=" font-bold leading-[26px] text-[rgb(48,48,48)]">
                ORDER SUMMARY
              </h3>
            </div>
            <OrderSummary
              orderBumpsInCheckout={orderBumpsInCheckout}
              checkout={checkout}
              pluginConfig={pluginConfig}
              bundles={bundles}
              isLoading={isLoading}
              preferLineDerivedTotal={hasOptimisticOverlay}
              lineItems={lineItems}
            />

            <div ref={paymentSectionRef}>
              <PaymentForm
                form={form}
                paymentError={paymentError}
                couponCode={couponCodeInput}
                onCouponCodeChange={setCouponCodeInput}
                onApplyCoupon={applyCoupon}
                onRemoveCoupon={removeCoupon}
                isApplyingCoupon={isApplyingCoupon}
                couponError={couponError}
                appliedCouponLabel={couponDisplayLabel}
                canRemoveCoupon={canRemoveCoupon}
              />
            </div>

            <div className="space-y-5 bg-white sm:space-y-6">
              <div>
                <button
                  type="button"
                  className={`w-full touch-manipulation rounded-lg bg-[rgb(255,215,18)] px-4 py-4 sm:py-6 md:py-8 lg:py-[35px] text-lg sm:text-xl md:text-[22px] font-bold text-black shadow-md transition-all duration-200 hover:bg-[rgb(255,215,18)]/90 hover:shadow-lg ${
                    isPaymentLoading ? "cursor-not-allowed opacity-50" : ""
                  }`}
                  onClick={handlePayment}
                  disabled={isPaymentLoading}
                >
                  {isPaymentLoading ? (
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="flex items-center gap-2">
                        <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-black sm:h-6 sm:w-6"></div>
                        Processing Payment...
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1.5 sm:gap-2.5">
                      <span className="text-center text-lg sm:text-xl md:text-[22px] font-bold text-black">
                        COMPLETE PURCHASE
                      </span>
                      <span className="text-center text-[11px] sm:text-[12px] text-black font-normal">
                        TRY IT RISK FREE! - 90 DAY MONEY BACK GUARANTEE!
                      </span>
                    </div>
                  )}
                </button>
                <div className="mt-2 flex flex-col items-center gap-2">
                  <p className="text-xs sm:text-sm text-[rgb(130,130,130)] text-center px-2">
                    By completing the payment, the client is in agreement with
                    our Terms of Service and Refund Policy.
                  </p>

                  <div className="flex items-center justify-center mt-2.5">
                    <img
                      crossOrigin="anonymous"
                      src="//assets.funnelkonnekt.com/92dcb740-0f76-11ee-81cb-39883164e76c/1688303121365_1670859883_1__1_.png"
                      alt=""
                      className="w-full max-w-[350px] h-auto"
                    />
                  </div>

                  <div
                    data-v-7000fbd6=""
                    data-eid="103705"
                    className="element-wrapper"
                  >
                    <div data-v-7000fbd6="" className="container-wrapper">
                      <div className="container el-103705 flex flex-row items-start mt-[10px] p-0">
                        <div className="container-box cbox-103705-0 w-auto min-w-0 max-w-none inline-flex">
                          <div
                            data-v-7000fbd6=""
                            data-eid="201680"
                            className="element-wrapper"
                          >
                            <div
                              data-v-7000fbd6=""
                              className="img el-201680 flex items-center m-0"
                            >
                              <img
                                crossOrigin="anonymous"
                                src="//assets.funnelkonnekt.com/92dcb740-0f76-11ee-81cb-39883164e76c/1688303466399_1663184638_1628741616_lock.png"
                                data-src="//assets.funnelkonnekt.com/92dcb740-0f76-11ee-81cb-39883164e76c/1688303466399_1663184638_1628741616_lock.png"
                                alt=""
                                width="20px"
                                height="auto"
                                className="lazyloaded"
                              />
                            </div>
                          </div>
                          <div
                            data-v-7000fbd6=""
                            data-eid="740385"
                            className="element-wrapper"
                          >
                            <div
                              data-v-7000fbd6=""
                              className="paragraph el-740385 font-montserrat leading-normal mt-[3px] ml-[5px] p-0 text-[13px] text-center"
                            >
                              <div className="paragraph-inner">
                                <p>Secure 256-bit SSL encryption</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    data-v-7000fbd6=""
                    data-eid="587806"
                    className="element-wrapper "
                  >
                    <div data-v-7000fbd6="" className="container-wrapper">
                      <div className="container el-587806 flex flex-col md:flex-row items-center justify-center space-y-2.5 md:space-y-0 my-[15px] p-0 ">
                        <div className="container-box cbox-587806-0 w-[20%] min-w-[20%] max-w-[20%]">
                          <div
                            data-v-7000fbd6=""
                            data-eid="323221"
                            className="element-wrapper"
                          >
                            <div
                              data-v-7000fbd6=""
                              className="img el-323221 flex items-center m-0"
                            >
                              <img
                                crossOrigin="anonymous"
                                src="//assets.funnelkonnekt.com/c3586120-cca6-11ed-a5ff-e92bf37fd671/1687870531396_nooro90days.png"
                                data-src="//assets.funnelkonnekt.com/c3586120-cca6-11ed-a5ff-e92bf37fd671/1687870531396_nooro90days.png"
                                alt=""
                                className="w-full h-auto ls-is-cached lazyloaded"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="container-box cbox-587806-1 w-auto min-w-0 max-w-none ">
                          <div
                            data-v-7000fbd6=""
                            data-eid="532069"
                            className="element-wrapper"
                          >
                            <div
                              data-v-7000fbd6=""
                              className="paragraph el-532069 font-montserrat leading-normal mt-0 ml-[5px] p-0 text-[16px] text-center"
                            >
                              <div className="paragraph-inner">
                                <p className="text-center md:text-left">
                                  <strong>90 DAYS GUARANTEE</strong>
                                </p>
                              </div>
                            </div>
                          </div>
                          <div
                            data-v-7000fbd6=""
                            data-eid="306678"
                            className="element-wrapper"
                          >
                            <div
                              data-v-7000fbd6=""
                              className="paragraph el-306678 font-montserrat leading-normal mt-[10px] ml-[5px] p-0 text-[13px] text-center"
                            >
                              <div className="paragraph-inner">
                                <p className="text-center md:text-left">
                                  If you are not completely thrilled with your
                                  Sonicfit™ device - we are offering you a 90
                                  day guarantee on all purchases. Simply contact
                                  our customer support for a full refund or
                                  replacement.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Review />
          </div>
        </div>
      </main>

      <TrusteReviews />
      <FAQ />

      <CheckoutFooter />

      {/* Mobile Sticky Payment Button */}
      {/* {showStickyButton && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white shadow-lg lg:hidden">
          <div className="p-4">
            <div className="mb-3 flex items-center justify-center gap-2 text-xs text-gray-600">
              <span className="font-medium text-emerald-600">
                FREE Shipping
              </span>
              <span>•</span>
              <span>90-Day Guarantee</span>
              <span>•</span>
              <span>SSL Secured</span>
            </div>
            <button
              type="button"
              className={`w-full inline-flex items-center justify-center touch-manipulation rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-4 text-lg font-semibold text-white shadow-sm transition-all duration-200 hover:from-blue-700 hover:to-blue-800 hover:shadow-md ${
                isPaymentLoading ? "cursor-not-allowed opacity-50" : ""
              }`}
              onClick={handlePayment}
              disabled={isPaymentLoading}
              style={{ minHeight: "56px" }}
            >
              {isPaymentLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  Processing...
                </div>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Complete Purchase
                </>
              )}
            </button>
          </div>
        </div>
      )} */}
    </div>
  );
}
